const MAX_IMAGE_DIMENSION = 50000;
const MAX_IMAGE_AREA = 100000000;
const AVIF_MIME_TYPE = 'image/avif';
const PNG_MIME_TYPE = 'image/png';
const PNG_FILE_EXTENSION = '.png';
const IMAGE_FILE_NAME_PATTERN = /\.(avif|bmp|gif|jpe?g|png|svg|webp)$/i;

const isBrowserImageFile = (file: File) =>
  file.type.startsWith('image/') || IMAGE_FILE_NAME_PATTERN.test(file.name);

const isAvifFile = (file: File) =>
  file.type === AVIF_MIME_TYPE || file.name.toLowerCase().endsWith('.avif');

const getUploadError = (error: unknown) => {
  if (error instanceof Error) {
    return error;
  }

  if (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof error.message === 'string' &&
    error.message.trim()
  ) {
    return new Error(error.message);
  }

  if (typeof error === 'string' && error.trim()) {
    return new Error(error);
  }

  return new Error('Upload failed');
};

const updateFileExtension = (fileName: string, nextExtension: string) => {
  const sanitizedName = fileName.trim() || 'upload';
  const lastDotIndex = sanitizedName.lastIndexOf('.');

  if (lastDotIndex === -1) {
    return `${sanitizedName}${nextExtension}`;
  }

  return `${sanitizedName.slice(0, lastDotIndex)}${nextExtension}`;
};

const loadImageElement = (file: File): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    const objectUrl = URL.createObjectURL(file);

    const cleanup = () => {
      URL.revokeObjectURL(objectUrl);
    };

    image.onload = () => {
      cleanup();
      resolve(image);
    };

    image.onerror = () => {
      cleanup();
      reject(new Error(`Could not read image "${file.name}".`));
    };

    image.src = objectUrl;
  });

const assertImageWithinLimits = (file: File, image: HTMLImageElement) => {
  const width = image.naturalWidth;
  const height = image.naturalHeight;

  if (
    width > MAX_IMAGE_DIMENSION ||
    height > MAX_IMAGE_DIMENSION ||
    width * height > MAX_IMAGE_AREA
  ) {
    throw new Error(
      `Image is too large (${width}x${height}). Maximum dimension is ${MAX_IMAGE_DIMENSION}px and maximum area is ${MAX_IMAGE_AREA.toLocaleString()} pixels.`,
    );
  }
};

const convertAvifToPng = async (file: File, image: HTMLImageElement) => {
  const canvas = document.createElement('canvas');
  canvas.width = image.naturalWidth;
  canvas.height = image.naturalHeight;

  const context = canvas.getContext('2d');
  if (!context) {
    throw new Error('Could not prepare AVIF image for upload.');
  }

  context.drawImage(image, 0, 0);

  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(nextBlob => {
      if (!nextBlob) {
        reject(new Error('Could not convert AVIF image to PNG.'));
        return;
      }

      resolve(nextBlob);
    }, PNG_MIME_TYPE);
  });

  const convertedFile = new File([blob], updateFileExtension(file.name, PNG_FILE_EXTENSION), {
    type: PNG_MIME_TYPE,
    lastModified: file.lastModified,
  }) as File & { uid?: string };

  if ('uid' in file) {
    convertedFile.uid = (file as File & { uid?: string }).uid;
  }

  return convertedFile;
};

export const prepareAssetUploadFile = async (file: File): Promise<File> => {
  if (typeof window === 'undefined' || !isBrowserImageFile(file)) {
    return file;
  }

  const image = await loadImageElement(file);
  assertImageWithinLimits(file, image);

  if (!isAvifFile(file)) {
    return file;
  }

  return convertAvifToPng(file, image);
};

/** Packing-only hints: retain bytes, recognised MIME and provider identity unchanged. */
export const preparePackingVideoFile = (file: File): File => {
  const mime = file.type.toLowerCase();
  const aliases: Record<string, string> = {
    'video/matroska': 'video/mkv',
    'video/x-matroska': 'video/mkv',
    'video/x-msvideo': 'video/avi',
    'video/x-m4v': 'video/mpeg4',
  };
  const suffixTypes: Record<string, string> = {
    mp4: 'video/mp4',
    mov: 'video/quicktime',
    webm: 'video/webm',
    m4v: 'video/mpeg4',
    avi: 'video/avi',
    mkv: 'video/mkv',
    flv: 'video/flv',
  };
  const suffix = file.name.split('.').pop()?.toLowerCase() ?? '';
  const normalized =
    aliases[mime] ??
    (!mime || mime === 'application/octet-stream' ? suffixTypes[suffix] : undefined);
  if (!normalized || normalized === mime) return file;
  // File wrapping changes metadata only; no decode/re-encode or additional byte copy.
  return new File([file], file.name, { type: normalized, lastModified: file.lastModified });
};

export const normalizeAssetUploadError = (error: unknown): Error & { retryable?: boolean } => {
  const result = getUploadError(error) as Error & { retryable?: boolean };
  const statusCode = Number((error as { statusCode?: number })?.statusCode);
  if (statusCode === 3502 || statusCode === 3507) {
    result.retryable = false;
    result.message = `${result.message} Remove this file and select a supported video; retrying the same bytes will not fix it.`;
  }
  return result;
};
